import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BookOpen, Download, Shield, CheckCircle2, FileText, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

const DISCIPLINE_LABELS = {
  muay_thai_kickboxing: { label: "Kickboxing / Muay Thaï", emoji: "🥊", color: "text-impact-red" },
  jiu_jitsu_brésilien: { label: "Jiu-Jitsu Brésilien", emoji: "🤼", color: "text-blue-600" },
  jiu_jitsu_bresilien: { label: "Jiu-Jitsu Brésilien", emoji: "🤼", color: "text-blue-600" },
  jiu_jitsu_kids: { label: "Jiu-Jitsu Kids", emoji: "🧒", color: "text-yellow-500" },
  jiu_jitsu_can_ryu: { label: "Jiu-jitsu Can Ryu", emoji: "🥋", color: "text-impact-gold" },
};

const fileIcon = (mime) => {
  if (["pdf"].includes(mime)) return <FileText size={16} className="text-impact-red"/>;
  if (["jpg", "jpeg", "png", "webp"].includes(mime)) return <ImageIcon size={16} className="text-blue-600"/>;
  return <FileText size={16} className="text-muted-foreground"/>;
};

const formatSize = (b) => {
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / (1024 * 1024)).toFixed(2)} Mo`;
};

export default function CurriculumLibraryModule() {
  const [data, setData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/curriculums/list");
      setData(r.data);
    } catch { setData({ count: 0, disciplines: [] }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await api.get("/curriculums/verify");
      setVerifyResult(r.data);
      if (r.data.corrupt.length === 0 && r.data.missing.length === 0) {
        toast.success(`✓ ${r.data.ok}/${r.data.total} fichiers intègres`);
      } else {
        toast.error(`⚠ ${r.data.corrupt.length} altérés, ${r.data.missing.length} manquants`);
      }
    } catch { toast.error("Erreur vérification"); }
    setVerifying(false);
  };

  const download = async (fileHash, filename) => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const tok = localStorage.getItem("map_token") || "";
      const r = await fetch(`${apiBase}/api/curriculums/${fileHash}/download`, { headers: { "Authorization": `Bearer ${tok}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur téléchargement"); }
  };

  return (
    <div data-testid="curriculum-library-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Bibliothèque Curriculums"
          icon={BookOpen}
          breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Curriculums" }]}
          subtitle="Tous tes curriculums archivés avec hash SHA-256 (préservation immuable)."
          actions={
            <div className="flex flex-col items-end gap-2">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{data?.count ?? "—"} FICHIERS</div>
              <button data-testid="verify-btn" onClick={verify} disabled={verifying}
                className="bg-amber-500 text-black hover:bg-amber-400 text-sm px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors">
                <Shield size={14}/> {verifying ? "Vérification..." : "Vérifier l'intégrité"}
              </button>
            </div>
          }
        />
      </div>

      {verifyResult && (
        <div className="hard-border-b bg-secondary/20 px-8 lg:px-12 py-4" data-testid="verify-result">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1"><CheckCircle2 size={16} className="text-[#10b981]"/> {verifyResult.ok}/{verifyResult.total} intègres</span>
            {verifyResult.corrupt.length > 0 && (
              <span className="text-impact-red flex items-center gap-1"><AlertTriangle size={16}/> {verifyResult.corrupt.length} altérés</span>
            )}
            {verifyResult.missing.length > 0 && (
              <span className="text-impact-red flex items-center gap-1"><AlertTriangle size={16}/> {verifyResult.missing.length} manquants</span>
            )}
          </div>
        </div>
      )}

      <div className="px-8 lg:px-12 py-6 space-y-8">
        {!data || data.disciplines.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <BookOpen size={48} className="mx-auto mb-3 opacity-50"/>
            <p>Aucun curriculum archivé.</p>
            <p className="text-xs mt-2">Lancer <code className="bg-secondary px-1">python3 scripts/preserve_curriculums.py archive</code></p>
          </div>
        ) : (
          data.disciplines.map((grp) => {
            const meta = DISCIPLINE_LABELS[grp.discipline] || { label: grp.discipline, emoji: "🥋", color: "text-foreground" };
            return (
              <section key={grp.discipline} data-testid={`section-${grp.discipline}`}>
                <h2 className={`font-display font-black text-2xl mb-3 flex items-center gap-2 ${meta.color}`}>
                  <span>{meta.emoji}</span> {meta.label}
                  <span className="ticker text-xs text-muted-foreground ml-2">{grp.count} fichiers</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grp.files.map((f) => (
                    <article key={f.file_hash} className="hard-border p-3 hover:shadow-md transition-shadow" data-testid={`file-${f.file_hash.slice(0, 12)}`}>
                      <div className="flex items-start gap-2 mb-2">
                        {fileIcon(f.mime_hint)}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate" title={f.filename}>{f.filename}</div>
                          <div className="text-xs text-muted-foreground">{formatSize(f.size_bytes)} · {f.mime_hint?.toUpperCase()}</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono mb-2 truncate" title={f.file_hash}>
                        🔒 {f.file_hash.slice(0, 24)}...
                      </div>
                      <button onClick={() => download(f.file_hash, f.filename)}
                        data-testid={`dl-${f.file_hash.slice(0, 12)}`}
                        className="w-full bg-secondary hover:bg-impact-gold hover:text-black ticker text-xs py-1.5 flex items-center justify-center gap-1 transition-colors">
                        <Download size={12}/> Télécharger
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
